import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Phone, Mail, MapPin, MessageCircle, Facebook, Instagram, Twitter, Linkedin, Award, Shield, Clock, Star } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, 
    ${brandTheme.colors.secondary[900]} 0%, 
    ${brandTheme.colors.secondary[800]} 50%, 
    ${brandTheme.colors.primary[900]} 100%
  );
  color: ${brandTheme.colors.neutral.white};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(254, 206, 27, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(254, 206, 27, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(254, 206, 27, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(254, 206, 27, 0.3) 20%, 
      rgba(254, 206, 27, 0.6) 50%, 
      rgba(254, 206, 27, 0.3) 80%, 
      transparent 100%
    );
  }
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${brandTheme.spacing[4]};
  position: relative;
  z-index: 2;
`;

const FooterMain = styled.div`
  padding: ${brandTheme.spacing[20]} 0 ${brandTheme.spacing[12]};
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: ${brandTheme.spacing[8]};
  margin-bottom: ${brandTheme.spacing[12]};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: ${brandTheme.spacing[6]};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${brandTheme.spacing[8]};
  }
`;

const FooterSection = styled.div`
  h3 {
    color: ${brandTheme.colors.primary[400]};
    margin-bottom: ${brandTheme.spacing[4]};
    font-size: 1.125rem;
    font-weight: ${brandTheme.typography.fontWeight.semibold};
    letter-spacing: 0.025em;
    text-transform: uppercase;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -${brandTheme.spacing[2]};
      left: 0;
      width: 30px;
      height: 2px;
      background: linear-gradient(90deg, ${brandTheme.colors.primary[500]}, ${brandTheme.colors.primary[300]});
      border-radius: 1px;
    }

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: ${brandTheme.spacing[3]};
    line-height: 1.6;
    font-size: 0.95rem;
  }

  ul {
    list-style: none;
    margin-top: ${brandTheme.spacing[4]};
  }

  li {
    margin-bottom: ${brandTheme.spacing[2]};
  }

  a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    position: relative;
    display: inline-block;

    &:hover {
      color: ${brandTheme.colors.primary[300]};
      transform: translateX(4px);
    }

    &::before {
      content: '';
      position: absolute;
      left: -${brandTheme.spacing[3]};
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 1px;
      background: ${brandTheme.colors.primary[400]};
      transition: width 0.3s ease;
    }

    &:hover::before {
      width: 8px;
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[3]};
  margin-bottom: ${brandTheme.spacing[4]};
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;

  &:hover {
    color: ${brandTheme.colors.primary[300]};
    transform: translateX(4px);
  }

  svg {
    color: ${brandTheme.colors.primary[400]};
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  &:hover svg {
    color: ${brandTheme.colors.primary[300]};
    transform: scale(1.1);
  }

  a {
    color: inherit;
    text-decoration: none;
    font-size: 0.9rem;
  }
`;

const ServiceAreas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${brandTheme.spacing[2]};
  margin-top: ${brandTheme.spacing[4]};

  span {
    background: rgba(254, 206, 27, 0.15);
    padding: ${brandTheme.spacing[2]} ${brandTheme.spacing[3]};
    border-radius: ${brandTheme.borderRadius.lg};
    font-size: 0.8rem;
    border: 1px solid rgba(254, 206, 27, 0.3);
    color: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(254, 206, 27, 0.25);
      border-color: rgba(254, 206, 27, 0.5);
      transform: translateY(-2px);
      color: ${brandTheme.colors.neutral.black};
    }
  }
`;

const BottomBar = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  border-top: 1px solid rgba(254, 206, 27, 0.3);
  padding: ${brandTheme.spacing[6]} 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, 
    rgba(0, 0, 0, 0.9) 0%, 
    rgba(0, 0, 0, 0.95) 50%, 
    rgba(0, 0, 0, 0.9) 100%
  );
  backdrop-filter: blur(20px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(254, 206, 27, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 50%, rgba(254, 206, 27, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const BottomBarContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${brandTheme.spacing[4]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${brandTheme.spacing[4]};
  position: relative;
  z-index: 2;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: ${brandTheme.spacing[3]};
    padding: 0 ${brandTheme.spacing[3]};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${brandTheme.spacing[3]};
  margin-top: ${brandTheme.spacing[6]};

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);

    &:hover {
      background: ${brandTheme.colors.primary[500]};
      color: ${brandTheme.colors.neutral.black};
      transform: translateY(-3px) scale(1.05);
      box-shadow: 0 10px 25px rgba(254, 206, 27, 0.4);
    }
  }
`;

const CompanyInfo = styled.div`
  margin-top: ${brandTheme.spacing[6]};
  padding-top: ${brandTheme.spacing[6]};
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  h4 {
    color: ${brandTheme.colors.primary[300]};
    margin-bottom: ${brandTheme.spacing[3]};
    font-size: 1rem;
    font-weight: ${brandTheme.typography.fontWeight.semibold};
  }

  p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
    margin-bottom: ${brandTheme.spacing[2]};
    line-height: 1.5;
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: ${brandTheme.spacing[4]};
  flex-wrap: wrap;

  a {
    color: rgba(255, 255, 255, 0.6);
    text-decoration: none;
    font-size: 0.85rem;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      color: ${brandTheme.colors.primary[300]};
    }

    &:not(:last-child)::after {
      content: '|';
      position: absolute;
      right: -${brandTheme.spacing[2]};
      color: rgba(255, 255, 255, 0.3);
    }
  }

  @media (max-width: 768px) {
    justify-content: center;
    gap: ${brandTheme.spacing[3]};
  }
`;

const TrustBadges = styled.div`
  display: flex;
  gap: ${brandTheme.spacing[4]};
  margin-top: ${brandTheme.spacing[6]};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[2]};
  background: rgba(255, 255, 255, 0.05);
  padding: ${brandTheme.spacing[2]} ${brandTheme.spacing[3]};
  border-radius: ${brandTheme.borderRadius.lg};
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  svg {
    color: ${brandTheme.colors.primary[400]};
    width: 16px;
    height: 16px;
  }

  span {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.8rem;
    font-weight: ${brandTheme.typography.fontWeight.medium};
  }
`;

const PartnershipsSection = styled.div`
  margin-top: ${brandTheme.spacing[6]};
  padding-top: ${brandTheme.spacing[6]};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;

  h4 {
    color: ${brandTheme.colors.primary[300]};
    margin-bottom: ${brandTheme.spacing[4]};
    font-size: 0.9rem;
    font-weight: ${brandTheme.typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const PartnershipsLogos = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${brandTheme.spacing[4]};
  align-items: center;

  @media (max-width: 768px) {
    gap: ${brandTheme.spacing[3]};
  }
`;

const PartnershipLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${brandTheme.spacing[2]};

  a {
    display: block;
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }

  img {
    max-width: 100px;
    height: auto;
    filter: brightness(1.1) contrast(1.1);
    transition: all 0.3s ease;

    &:hover {
      filter: brightness(1.2) contrast(1.2);
    }
  }

  span {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
    font-weight: ${brandTheme.typography.fontWeight.medium};
    text-align: center;
    line-height: 1.3;
  }
`;

const Footer: React.FC = () => {
  const serviceAreas = [
    'London', 'Surrey', 'Hertfordshire', 'Buckinghamshire', 
    'Kent', 'Essex', 'Berkshire', 'Oxfordshire'
  ];

  return (
    <FooterContainer>
      <FooterContent>
        <FooterMain>
          <FooterGrid>
            <FooterSection>
              <h3>ZAN RESIN SURFACES LTD</h3>
              <p>
                Leading specialists in resin bound driveways, patios, and paving across London, Surrey, 
                and Hertfordshire. We design smooth, SUDS-compliant surfaces that elevate kerb appeal, 
                maximise usable outdoor space, and stay beautiful for decades with a 10-year warranty.
              </p>
              
              <TrustBadges>
                <TrustBadge>
                  <Award size={16} />
                  <span>10 Year Warranty</span>
                </TrustBadge>
                <TrustBadge>
                  <Shield size={16} />
                  <span>Fully Insured</span>
                </TrustBadge>
                <TrustBadge>
                  <Clock size={16} />
                  <span>Free Quotes</span>
                </TrustBadge>
              </TrustBadges>

              <PartnershipsSection>
                <h4>Proud Partners with</h4>
                <PartnershipsLogos>
                  <PartnershipLogo>
                    <a href="https://www.vubasurfaces.co.uk/" target="_blank" rel="noopener noreferrer">
                      <img src="/vuba_resin_logo_uk.png" alt="Vuba Surfaces - UK's #1 Resin Bound Surfacing Manufacturer" />
                    </a>
                    <span>UK's #1 Resin Bound Manufacturer</span>
                  </PartnershipLogo>
                  <PartnershipLogo>
                    <a href="https://www.gclproducts.co.uk/" target="_blank" rel="noopener noreferrer">
                      <img src="/gcl_products_logo_uk.png" alt="GCL Products - Groundworks, Construction & Landscaping" />
                    </a>
                    <span>Groundworks & Construction Products</span>
                  </PartnershipLogo>
                </PartnershipsLogos>
              </PartnershipsSection>

              <SocialLinks>
                <a href="#" aria-label="Facebook">
                  <Facebook size={18} />
                </a>
                <a href="#" aria-label="Instagram">
                  <Instagram size={18} />
                </a>
                <a href="#" aria-label="Twitter">
                  <Twitter size={18} />
                </a>
                <a href="#" aria-label="LinkedIn">
                  <Linkedin size={18} />
                </a>
              </SocialLinks>
            </FooterSection>

            <FooterSection>
              <h3>Our Services</h3>
              <ul>
                <li><Link to="/resin-driveways-london/">Resin Bound Driveways</Link></li>
                <li><Link to="/resin-pathways-london/">Resin Patios & Paving</Link></li>
                <li><Link to="/resin-pool-decks-london/">Pool Deck Surfacing</Link></li>
                <li><Link to="/what-is-resin-bound-surfacing/">What is Resin Bound?</Link></li>
                <li><Link to="/gallery/">Project Gallery</Link></li>
                <li><Link to="/about-us/">About Us</Link></li>
                <li><Link to="/blog/">Blog &amp; guides</Link></li>
                <li><Link to="/blog/#london-borough-guides">London borough guides</Link></li>
              </ul>
            </FooterSection>

            <FooterSection>
              <h3>Service Areas</h3>
              <ServiceAreas>
                {serviceAreas.map((area) => (
                  <span key={area}>{area}</span>
                ))}
              </ServiceAreas>
              
              <p style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                We also cover surrounding areas within a 50-mile radius of London. 
                Contact us for coverage in your specific location.
              </p>
            </FooterSection>

            <FooterSection>
              <h3>Contact Information</h3>
              <ContactItem>
                <Phone size={18} />
                <a href="tel:+447535935779">07535 935 779</a>
              </ContactItem>
              <ContactItem>
                <Mail size={18} />
                <a href="mailto:info@resinsurfaceslondon.co.uk">info@resinsurfaceslondon.co.uk</a>
              </ContactItem>
              <ContactItem>
                <MapPin size={18} />
                <span>London, Surrey & Hertfordshire</span>
              </ContactItem>
              <ContactItem>
                <MessageCircle size={18} />
                <a href="https://wa.me/447535935779">WhatsApp Chat</a>
              </ContactItem>

              <CompanyInfo>
                <h4>Company Details</h4>
                <p>Company Number: 16698534</p>
                <p>Registered Office: Broadfield Broadhurst Gardens, London, England, NW6 3BN</p>
              </CompanyInfo>
            </FooterSection>
          </FooterGrid>
        </FooterMain>

        <BottomBar>
          <BottomBarContent>
            <div>
              <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem', fontWeight: '500' }}>
                &copy; 2025 ZAN RESIN SURFACES LTD. All rights reserved.
              </p>
            </div>
            <LegalLinks>
              <Link to="/privacy-policy/">Privacy Policy</Link>
              <Link to="/terms-conditions/">Terms & Conditions</Link>
              <Link to="/cookies/">Cookie Policy</Link>
            </LegalLinks>
          </BottomBarContent>
        </BottomBar>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
